import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, ReferenceLine, YAxis } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GAIN = "#34D399";
const LOSS = "#F87171";

const windowLabel = (d) => (d >= 365 ? "the last 12 months" : `the last ${d} days`);

function TeaserShell({ testId, children }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-8 sm:p-10" style={{ minHeight: 200 }} data-testid={testId}>
      {children}
    </section>
  );
}

export default function PortfolioTeaser() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let tries = 0;
    let timer;
    const load = () => axios.get(`${BACKEND_URL}/api/portfolio`).then((r) => {
      if (r.data?.warming && tries < 6) { tries += 1; timer = setTimeout(load, 5000); return; }
      setData(r.data);
    }).catch(() => setData({ error: true }));
    load();
    return () => clearTimeout(timer);
  }, []);

  if (!data) {
    return <TeaserShell testId="portfolio-teaser-loading"><p className="text-lg text-neutral-400 animate-pulse">Replaying every alert…</p></TeaserShell>;
  }
  if (data.error || data.warming || !data.curve || data.curve.length < 2) {
    return (
      <TeaserShell testId="portfolio-teaser-empty">
        <p className="text-lg text-neutral-300">
          {data.error ? "Couldn’t reach the desk right now. Refresh in a moment." : "The simulation is being rebuilt right now. The full replay is always on the portfolio page."}
        </p>
        <Link to="/portfolio" className="mt-4 inline-flex items-center gap-1 text-violet-300 hover:underline underline-offset-4" data-testid="portfolio-teaser-empty-link">See the full simulation <ArrowRight className="w-4 h-4" /></Link>
      </TeaserShell>
    );
  }

  const isWin = data.return_pct >= 0;
  const spy = data.spy_return_pct || 0;
  const gap = data.return_pct - spy;
  const color = isWin ? GAIN : LOSS;

  return (
    <section className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-8 sm:p-10" data-testid="portfolio-teaser">
      <div className="flex flex-col items-center text-center gap-8">
        <div>
          <p className="text-neutral-400">$10,000 · {windowLabel(data.window_days)} · every alert taken</p>
          <div className="mt-3 font-serif-hero text-7xl sm:text-8xl tabular-nums leading-none" style={{ color }} data-testid="portfolio-teaser-return">
            {isWin ? "+" : ""}{data.return_pct.toFixed(2)}%
          </div>
          <p className="mt-3 text-sm text-neutral-300" data-testid="portfolio-teaser-sim-note">Simulated results, not real returns.</p>
          <p className="mt-5 text-neutral-200" data-testid="portfolio-teaser-vs-spy">
            Now ${data.final_equity.toLocaleString(undefined, { maximumFractionDigits: 0 })} · S&amp;P 500 <span style={{ color: spy >= 0 ? GAIN : LOSS }}>{spy >= 0 ? "+" : ""}{spy}%</span>
            <span className="text-neutral-400"> · </span>
            <span style={{ color: gap >= 0 ? GAIN : LOSS }}>{gap >= 0 ? "+" : ""}{gap.toFixed(1)} pts {gap >= 0 ? "ahead" : "behind"}</span>
          </p>
          <p className="mt-2 text-sm text-neutral-400">{data.wins} {data.wins === 1 ? "win" : "wins"} · {data.losses} {data.losses === 1 ? "loss" : "losses"} · {data.alerts_followed} of {data.alerts_published} alerts taken</p>
          <Link to="/portfolio" className="mt-7 inline-flex items-center gap-2 rounded-full px-6 h-11 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors" data-testid="portfolio-teaser-cta">
            See our math <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="w-full h-48 sm:h-56">
          <ResponsiveContainer>
            <AreaChart data={data.curve} margin={{ top: 8, right: 4, left: 4, bottom: 4 }}>
              <defs>
                <linearGradient id="teaser-eq" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis hide domain={[(min) => Math.min(data.seed, min) - Math.max(6, Math.abs(min) * 0.02), (max) => max + Math.max(6, Math.abs(max) * 0.02)]} />
              <ReferenceLine y={data.seed} stroke="rgba(148,163,184,0.5)" strokeDasharray="3 4" />
              <Area type="stepAfter" dataKey="equity" stroke={color} strokeWidth={2} fill="url(#teaser-eq)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <p className="mt-6 pt-4 border-t border-white/10 text-xs text-neutral-500 text-center" data-testid="portfolio-teaser-fineprint">
        Four positions at a time · 25% each · 0.10% slippage per side · no commissions · fractional shares. Simulated. Past performance doesn’t predict future results.
      </p>
    </section>
  );
}
